import { useEffect, useState } from 'react';
import type { MensajeDeConversacion, MiConversacion, MiPerfil, OperacionesClienteFinal } from '../api/ops/clienteFinal';
import { ErrorApi } from '../api/problema';
import { Chip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { BotonPc, Pagina } from './Pagina';

/**
 * «Mis conversaciones» (11.10, `/mis-conversaciones`): con el token de cliente de «Mis datos» (2.9), el negocio a elegir
 * entre sus perfiles (`listMyBusinessProfiles`, G35) y lo que ha hablado con él por cualquier canal
 * (`listMyConversations`, `listMyConversationMessages`). La conversación abierta arriba en grande y las anteriores debajo;
 * desde aquí no se escribe: se sigue por el canal. Mensajes borrados por la retención sin contenido (G204).
 *
 * Con G687: el extracto del último mensaje (`last_message_excerpt`) en cada conversación y «Escribir por…» con el enlace
 * público del canal (`channel_url`, nulo si el canal ya no está activo). Un código de un solo uso llega ya como «••••••»
 * (`redacted`, G692). Lo que la API no da y no se pinta: el módulo de cada mensaje automático (la etiqueta «· Reservas»,
 * G586) y el texto del aviso de privacidad que llevó una respuesta (`privacy_notice_version` solo trae la versión).
 */
const CANAL: Readonly<Record<string, readonly [string, NombreIcono]>> = {
    whatsapp: ['WhatsApp', 'message-circle'],
    instagram: ['Instagram', 'camera'],
    telegram: ['Telegram', 'send'],
    email: ['Email', 'mail'],
    web_chat: ['Chat de la web', 'globe'],
    voice: ['Teléfono', 'phone'],
};
const canal = (t: string): readonly [string, NombreIcono] => CANAL[t] ?? [t, 'message-circle'];

const minuscula = (t: string) => t.charAt(0).toLocaleLowerCase('es') + t.slice(1);

/** «Hoy, 12:40», «Ayer, 21:14», «3 oct» o «14 sep 2024». */
function cuandoConversacion(iso: string, ahora: Date): string {
    const f = new Date(iso);
    if (Number.isNaN(f.getTime())) {
        return '';
    }
    const hora = f.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const ayer = new Date(ahora.getTime() - 86_400_000);
    if (f.toDateString() === ahora.toDateString()) {
        return `Hoy, ${hora}`;
    }
    if (f.toDateString() === ayer.toDateString()) {
        return `Ayer, ${hora}`;
    }
    const opciones: Intl.DateTimeFormatOptions = f.getFullYear() === ahora.getFullYear() ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' };
    return f.toLocaleDateString('es-ES', opciones).replace('.', '');
}

function dia(iso: string, ahora: Date): string {
    const f = new Date(iso);
    const ayer = new Date(ahora.getTime() - 86_400_000);
    if (f.toDateString() === ahora.toDateString()) {
        return 'Hoy';
    }
    if (f.toDateString() === ayer.toDateString()) {
        return 'Ayer';
    }
    const t = f.toLocaleDateString('es-ES', f.getFullYear() === ahora.getFullYear() ? { weekday: 'long', day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' }).replace(',', '');
    return t.charAt(0).toLocaleUpperCase('es') + t.slice(1);
}

/** «Escribir por WhatsApp» con el enlace del canal (`wa.me`, `ig.me`, `t.me` o la web del negocio con el chat, G687). */
function EscribirPor({ c }: { c: MiConversacion }) {
    const url = c.channel_url ?? null;
    if (url === null) {
        return null;
    }
    const texto = c.channel_type === 'web_chat' ? 'Abrir el chat de la web' : `Escribir por ${canal(c.channel_type)[0]}`;
    return (
        <a className="rv-pc-b rv-pc-b--sec rv-pc-b--ancho rv-pc-cv__escribir" href={url} target="_blank" rel="noreferrer">
            <Icono nombre={canal(c.channel_type)[1]} />
            <span>{texto}</span>
        </a>
    );
}

const textoEstado = (c: MiConversacion) => (c.state === 'human' ? 'Te atiende una persona del equipo' : c.state === 'assistant' ? 'Te atiende el asistente' : 'Cerrada');

interface Props {
    ops: OperacionesClienteFinal;
    onSalir: () => void;
    onCaducada: () => void;
    /** Un perfil que no es suyo (404): como sin identificar. */
    onNoEsSuyo: () => void;
    /** «Mis datos», del pie. */
    onMisDatos: () => void;
    ahora?: () => Date;
}

interface Lista {
    clave: string;
    items: MiConversacion[];
    siguiente: string | null;
    error: boolean;
}

export function MisConversaciones({ ops, onSalir, onCaducada, onNoEsSuyo, onMisDatos, ahora: reloj }: Props) {
    const ahora = (reloj ?? (() => new Date()))();
    const [perfiles, setPerfiles] = useState<MiPerfil[] | null>(null);
    const [errorPerfiles, setErrorPerfiles] = useState(false);
    const [elegido, setElegido] = useState<string | null>(null);
    const [lista, setLista] = useState<Lista | null>(null);
    const [intento, setIntento] = useState(0);
    const [masOcupado, setMasOcupado] = useState(false);
    const [abierta, setAbierta] = useState<string | null>(null);

    const fallo = (e: unknown, si: () => void) => {
        if (e instanceof ErrorApi && e.estado === 401) {
            onCaducada();
        } else if (e instanceof ErrorApi && e.estado === 404) {
            onNoEsSuyo();
        } else {
            si();
        }
    };

    useEffect(() => {
        let vivo = true;
        ops.listMyBusinessProfiles({ limit: 100 })
            .then((l) => { if (vivo) { setPerfiles(l.data.filter((p) => p.ended == null)); setErrorPerfiles(false); } })
            .catch((e: unknown) => { if (vivo) fallo(e, () => { setErrorPerfiles(true); }); });
        return () => { vivo = false; };
        // Los avisos cambian en cada pintado: solo se mira al cargar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, intento]);

    const perfil = perfiles?.find((p) => p.customer_id === elegido) ?? perfiles?.[0] ?? null;
    const idPerfil = perfil?.customer_id ?? null;
    const clave = `${idPerfil ?? ''}|${String(intento)}`;
    useEffect(() => {
        if (idPerfil === null) {
            return;
        }
        let vivo = true;
        ops.listMyConversations(idPerfil, { limit: 20 })
            .then((l) => { if (vivo) setLista({ clave, items: l.data, siguiente: l.next_cursor, error: false }); })
            .catch((e: unknown) => { if (vivo) fallo(e, () => { setLista({ clave, items: [], siguiente: null, error: true }); }); });
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, idPerfil, clave]);

    const nombre = perfil?.business.name ?? '';
    const cabecera = perfil === null ? ({ tipo: 'cuenta' } as const) : ({ tipo: 'negocio', nombre, linea: 'Mis conversaciones' } as const);
    const pie = perfil === null ? ({ tipo: 'cuenta' } as const) : ({ tipo: 'negocio', nombre, privacidad: perfil.business.privacy_url ?? null } as const);
    const reintentar = () => { setErrorPerfiles(false); setIntento((i) => i + 1); };
    const actual = lista?.clave === clave ? lista : null;

    const verMas = () => {
        const cursor = actual?.siguiente ?? null;
        if (cursor === null || idPerfil === null) {
            return;
        }
        setMasOcupado(true);
        ops.listMyConversations(idPerfil, { cursor, limit: 20 })
            .then((l) => { setLista((x) => (x === null || x.clave !== clave ? x : { ...x, items: [...x.items, ...l.data], siguiente: l.next_cursor })); })
            .catch((e: unknown) => { fallo(e, () => undefined); })
            .finally(() => { setMasOcupado(false); });
    };

    const conversacion = abierta === null ? null : (actual?.items.find((c) => c.id === abierta) ?? null);
    if (conversacion !== null && idPerfil !== null) {
        return (
            <Pagina cabecera={cabecera} pie={pie} onSalir={onSalir} volver={{ texto: 'Mis conversaciones', accion: () => { setAbierta(null); } }}>
                <Conversacion key={conversacion.id} ops={ops} perfil={idPerfil} c={conversacion} negocio={nombre} ahora={ahora} fallo={fallo} />
            </Pagina>
        );
    }

    const error = errorPerfiles || actual?.error === true;
    const cargando = !error && (perfiles === null || (perfil !== null && actual === null));
    const abiertaArriba = actual?.items.find((c) => c.state !== 'closed') ?? null;
    const resto = (actual?.items ?? []).filter((c) => c !== abiertaArriba);

    return (
        <Pagina cabecera={cabecera} pie={pie} onSalir={onSalir} titulo={error ? undefined : { titulo: 'Mis conversaciones', sub: nombre === '' ? 'Lo que has hablado con el negocio, por cualquier canal.' : `Lo que has hablado con ${nombre}, por cualquier canal.` }}>
            {perfiles !== null && perfiles.length > 1 && (
                <label className="rv-pc-negocio__canal">
                    <span>Negocio</span>
                    <select className="rv-pc-select rv-pc-select--ancho" value={perfil?.customer_id ?? ''} onChange={(e) => { setElegido(e.target.value); setAbierta(null); }}>
                        {perfiles.map((p) => <option key={p.customer_id} value={p.customer_id}>{p.business.name}</option>)}
                    </select>
                </label>
            )}
            {error ? (
                <section className="rv-pc-card" role="alert">
                    <span className="rv-pc-cv__baldosa rv-pc-cv__baldosa--rojo" aria-hidden="true"><Icono nombre="triangle-alert" /></span>
                    <div className="rv-pc-card__cab"><h2>No hemos podido cargar tus conversaciones</h2><span>Puede ser la conexión. Tus mensajes están a salvo. Prueba de nuevo.</span></div>
                    <BotonPc texto="Reintentar" icono="refresh-cw" onClick={reintentar} />
                </section>
            ) : cargando ? (
                <section className="rv-pc-card" aria-busy="true">
                    {[0, 1, 2].map((i) => <div key={i} className="rv-pc-esqueleto" style={{ height: 56, marginTop: i === 0 ? 0 : 12 }} />)}
                </section>
            ) : perfil === null || (actual?.items.length ?? 0) === 0 ? (
                <section className="rv-pc-card">
                    <span className="rv-pc-cv__baldosa" aria-hidden="true"><Icono nombre="inbox" /></span>
                    <div className="rv-pc-card__cab">
                        <h2>{`Todavía no has hablado con ${nombre === '' ? 'el negocio' : nombre}`}</h2>
                        <span>Aquí verás lo que hables con ellos por WhatsApp, por el chat de su web o por los otros canales en los que te identifiques.</span>
                    </div>
                </section>
            ) : (
                <>
                    {abiertaArriba !== null && (
                        <section className="rv-pc-card" aria-label="Tu conversación abierta">
                            <span className="rv-pc-cv__ceja">Tu conversación abierta</span>
                            <h2 className="rv-pc-cv__grande">{textoEstado(abiertaArriba)}</h2>
                            <strong className="rv-pc-cv__por">{`Por ${canal(abiertaArriba.channel_type)[0]} · ${minuscula(cuandoConversacion(abiertaArriba.last_message_at, ahora))}`}</strong>
                            {(abiertaArriba.last_message_excerpt ?? null) !== null && <span className="rv-pc-cv__extracto">{abiertaArriba.last_message_excerpt}</span>}
                            <span className="rv-pc-cv__sub">
                                {abiertaArriba.state === 'human'
                                    ? `Te contestamos por ${canal(abiertaArriba.channel_type)[0]}: los mensajes del equipo llevan el nombre de ${nombre}.`
                                    : `Te contestamos por ${canal(abiertaArriba.channel_type)[0]}.`}
                            </span>
                            <BotonPc texto="Ver la conversación" icono="message-circle" onClick={() => { setAbierta(abiertaArriba.id); }} />
                            <EscribirPor c={abiertaArriba} />
                        </section>
                    )}
                    {resto.length > 0 && (
                        <>
                            {abiertaArriba !== null && <h2 className="rv-final-osc rv-pc-cv__titulo">Anteriores</h2>}
                            <section className="rv-pc-card rv-pc-card--seccion" aria-label="Anteriores">
                                {resto.map((c) => {
                                    const [n, icono] = canal(c.channel_type);
                                    const cuando = cuandoConversacion(c.last_message_at, ahora);
                                    return (
                                        <button key={c.id} type="button" className="rv-pc-cv__fila" onClick={() => { setAbierta(c.id); }}>
                                            <span className="rv-pc-pago__icono" aria-hidden="true"><Icono nombre={icono} /></span>
                                            <span>
                                                <strong>{n}</strong>
                                                <small>{c.state === 'closed' || c.state === undefined ? cuando : `${cuando} · ${textoEstado(c)}`}</small>
                                                {(c.last_message_excerpt ?? null) !== null && <span className="rv-pc-cv__extracto rv-pc-cv__extracto--fila">{c.last_message_excerpt}</span>}
                                            </span>
                                            <Icono nombre="chevron-right" />
                                        </button>
                                    );
                                })}
                                {actual?.siguiente != null && (
                                    <div className="rv-pc-paginas">
                                        <BotonPc texto="Ver anteriores" icono="chevron-down" tipo="sec" peq ocupado={masOcupado} onClick={verMas} />
                                    </div>
                                )}
                            </section>
                        </>
                    )}
                    <p className="rv-pc-nota rv-final-osc">
                        <Icono nombre="shield-check" />
                        <span>
                            {`Solo tú y el equipo de ${nombre} las podéis leer. Se borran pasado el plazo que guarda el negocio; si quieres que las borre antes, pídelo en `}
                            <button type="button" className="rv-pc-cv__enlace" onClick={onMisDatos}>Mis datos</button>.
                        </span>
                    </p>
                </>
            )}
        </Pagina>
    );
}

function Conversacion({ ops, perfil, c, negocio, ahora, fallo }: { ops: OperacionesClienteFinal; perfil: string; c: MiConversacion; negocio: string; ahora: Date; fallo: (e: unknown, si: () => void) => void }) {
    const [mensajes, setMensajes] = useState<MensajeDeConversacion[] | null>(null);
    const [siguiente, setSiguiente] = useState<string | null>(null);
    const [error, setError] = useState(false);
    const [intento, setIntento] = useState(0);
    const [ocupado, setOcupado] = useState(false);

    useEffect(() => {
        let vivo = true;
        ops.listMyConversationMessages(perfil, c.id, { limit: 50 })
            .then((l) => { if (vivo) { setMensajes([...l.data].reverse()); setSiguiente(l.next_cursor); setError(false); } })
            .catch((e: unknown) => { if (vivo) fallo(e, () => { setError(true); }); });
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, perfil, c.id, intento]);

    const anteriores = () => {
        if (siguiente === null) {
            return;
        }
        setOcupado(true);
        ops.listMyConversationMessages(perfil, c.id, { cursor: siguiente, limit: 50 })
            .then((l) => { setMensajes((x) => [...[...l.data].reverse(), ...(x ?? [])]); setSiguiente(l.next_cursor); })
            .catch((e: unknown) => { fallo(e, () => undefined); })
            .finally(() => { setOcupado(false); });
    };

    const [nombreCanal, icono] = canal(c.channel_type);
    const cerrada = c.state === 'closed' || c.state === undefined;
    const como =
        c.channel_type === 'web_chat'
            ? `Para seguir, abre el chat en la web de ${negocio}: empezará una conversación nueva.`
            : c.channel_type === 'email'
              ? `Para seguir, responde al último email de ${negocio}.`
              : `Para seguir hablando, escribe a ${negocio} por ${nombreCanal}: sigue en esta misma conversación.`;

    if (error) {
        return (
            <section className="rv-pc-card" role="alert">
                <span className="rv-pc-cv__baldosa rv-pc-cv__baldosa--rojo" aria-hidden="true"><Icono nombre="triangle-alert" /></span>
                <div className="rv-pc-card__cab"><h2>No hemos podido cargar la conversación</h2><span>Puede ser la conexión. Tus mensajes están a salvo. Prueba de nuevo.</span></div>
                <BotonPc texto="Reintentar" icono="refresh-cw" onClick={() => { setIntento((i) => i + 1); }} />
            </section>
        );
    }

    return (
        <>
            <section className="rv-pc-card" aria-labelledby="rv-pc-cv-t">
                <div className="rv-pc-cv__cab">
                    <span className="rv-pc-cv__baldosa rv-pc-cv__baldosa--tinta" aria-hidden="true"><Icono nombre={icono} /></span>
                    <span>
                        <h1 id="rv-pc-cv-t">{nombreCanal}</h1>
                        <small>{`Última actividad: ${minuscula(cuandoConversacion(c.last_message_at, ahora))}`}</small>
                    </span>
                    <Chip estado={cerrada ? 'neutral' : 'confirmed'}>{cerrada ? 'Cerrada' : 'Abierta'}</Chip>
                </div>
                <span className="rv-pc-cv__sub">
                    {cerrada ? 'Cerrada. Si vuelves a escribir por el mismo canal, se abre de nuevo.' : c.state === 'human' ? 'Te atiende una persona del equipo. Los mensajes del equipo llevan el nombre del negocio.' : 'Te atiende el asistente automático del negocio.'}
                </span>
            </section>
            {siguiente !== null && (
                <div className="rv-pc-paginas">
                    <BotonPc texto="Ver mensajes anteriores" icono="chevron-up" tipo="sec" peq ocupado={ocupado} onClick={anteriores} />
                </div>
            )}
            <div className="rv-pc-cv__log" role="log" aria-label="Mensajes" aria-busy={mensajes === null}>
                {mensajes === null && <div className="rv-pc-esqueleto" style={{ height: 56 }} />}
                {(mensajes ?? []).map((m, i, todos) => {
                    const previo: MensajeDeConversacion | undefined = i > 0 ? todos[i - 1] : undefined;
                    const nuevoDia = previo === undefined || new Date(previo.created_at).toDateString() !== new Date(m.created_at).toDateString();
                    const grupo = !nuevoDia && previo.origin === m.origin;
                    return (
                        <div key={m.id} className="rv-pc-cv__item">
                            {nuevoDia && <span className="rv-pc-cv__dia">{dia(m.created_at, ahora)}</span>}
                            <MensajeCliente m={m} negocio={negocio} grupo={grupo} />
                        </div>
                    );
                })}
            </div>
            <p className="rv-pc-nota rv-final-osc"><Icono nombre={icono} /><span>{como}</span></p>
            <EscribirPor c={c} />
        </>
    );
}

function MensajeCliente({ m, negocio, grupo }: { m: MensajeDeConversacion; negocio: string; grupo: boolean }) {
    const mio = m.origin === 'customer';
    const fuera = m.body == null && (m.media ?? []).length === 0;
    const quien = m.origin === 'assistant' ? 'Asistente' : m.origin === 'inbox' ? `${negocio} · equipo` : m.origin === 'entregas' || m.origin === 'system' ? `${negocio} · mensaje automático` : null;
    const hora = new Date(m.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    return (
        <div className={`rv-pc-cv__msg${mio ? ' rv-pc-cv__msg--mio' : ''}${fuera ? ' rv-pc-cv__msg--borrado' : ''}${grupo ? ' rv-pc-cv__msg--grupo' : ''}`}>
            {quien !== null && !grupo && <span className="rv-pc-cv__quien">{quien}</span>}
            <div className="rv-pc-cv__burbuja">
                {(m.media ?? []).map((x) => (
                    <span key={x.url} className="rv-pc-cv__adjunto"><Icono nombre={x.kind === 'image' ? 'camera' : 'paperclip'} />{x.kind === 'image' ? 'Foto' : 'Archivo'}</span>
                ))}
                <span className={m.redacted === true ? 'rv-pc-cv__codigo' : undefined}>{fuera ? 'Mensaje borrado: pasó el plazo de conservación del negocio.' : (m.body ?? '')}</span>
                <small>{hora}</small>
            </div>
            {m.origin === 'entregas' && <span className="rv-pc-cv__val">Texto provisional</span>}
        </div>
    );
}
